import type { Config } from 'tailwindcss'

import rebrandColors from './legacy-src/rebrand/colors'

const transformedRebrandColors = Object.keys(rebrandColors).reduce((acc, key) => {
  const colorVariants = rebrandColors[key]
  Object.keys(colorVariants).forEach((variant) => {
    const newKey = `${key}-${variant}`
    acc[newKey] = colorVariants[variant]
  })
  return acc
}, {})

const aiMessageTailBackground = (hex: string) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 46'%3E%3Cpath fill='%23${hex.replace('#', '')}' d='M42.5303 23.4412C44.2321 33.8828 53.8434 43.6863 59.5 46C37.7788 46 23.8636 38.4804 20.4697 33.8529L3.5 39.0588L5.19697 7H40.8333V13.9412V20.8824V22.6176C40.8333 26.0882 40.8333 13.0294 42.5303 23.4412Z'/%3E%3C/svg%3E")`

const promoNotchBackground = (hex: string) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 98 30'%3E%3Cpath fill='%23${hex.replace('#', '')}' d='M52.4389 25.9025L50 30L46.9183 25.3775C36.3487 9.52304 18.5547 0 -0.5 0H98C79.309 0 61.9988 9.84124 52.4389 25.9025Z'/%3E%3C/svg%3E")`

export default {
  darkMode: ['class'],
  content: [
    './pages/**/*.{js,ts,jsx,tsx}',
    './app/**/*.{js,ts,jsx,tsx}',
    './roots/**/*.{js,ts,jsx,tsx}',
    './src/**/*.{js,ts,jsx,tsx}',
    './legacy-src/**/*.{js,ts,jsx,tsx}',
    './legacy-src/styles/**/*.scss',
    './legacy-src/config/forms/*.json',
  ],
  theme: {
    colors: {
      primary: 'var(--color-primary)',
      secondary: 'var(--color-secondary)',
      white: '#FFFFFF',
      black: '#000000',
      transparent: 'transparent',
      'text-color': 'var(--color-text-color)',
      'body-bg': 'var(--color-body-bg)',
      error: 'var(--color-peach-800)',
      danger: 'var(--color-peach-800)',
      'hearme-purple': '#5B4CB2',
      ...transformedRebrandColors,
    },
    screens: {
      'big-phone': '450px',
      tablet: '768px',
      lg: '992px',
      'big-tablet': '1024px',
      'big-tablet-only': {
        min: '1024px',
        max: '1219.9px',
      },
      laptop: '1220px',
      xl: '1240px',
      desktop: '1440px',
    },
    extend: {
      backgroundImage: {
        'ai-message-tail-assistant': aiMessageTailBackground(rebrandColors.pink[500]),
        'ai-message-tail-user': aiMessageTailBackground(rebrandColors.sand[400]),
        'ai-promo-notch': promoNotchBackground(rebrandColors.sand[400]),
      },
      borderWidth: {
        6: '6px',
      },
      zIndex: {
        dialogOverlay: '1050',
        dialog: '1060',
      },
      boxShadow: {
        hedepy: '0 7px 30px rgb(211 155 123 / 30%)',
        rebrand: '0px 1.3px 3.9px 0px #0B47521A',
        cardShadow1: '0 1px 14px 0 rgba(0, 0, 0, 0.10)',
      },
      transitionTimingFunction: {
        'out-strong': 'cubic-bezier(0,0,0,1)',
      },
      animation: {
        'pulse-bg': 'pulseBg 3s infinite',
        'pulse-dot': 'pulseDot 2s ease-in-out infinite',
        'support-online-blink': 'supportOnlineBlink 2.8s ease-in-out infinite',
        'fade-in': 'fadeIn 2s ease-in-out forwards',
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'collapsible-down': 'collapsible-down 0.5s ease-out',
        'collapsible-up': 'collapsible-up 0.5s ease-out',
      },
      keyframes: {
        pulseDot: {
          '0%, 100%': { transform: 'scale(1)', opacity: '1' },
          '50%': { transform: 'scale(0.6)', opacity: '0.5' },
        },
        pulseBg: {
          '0%': {
            backgroundColor: '#2F747A',
          },
          '50%': {
            backgroundColor: '#F5F0EB',
          },
          '100%': {
            backgroundColor: '#2F747A',
          },
        },
        supportOnlineBlink: {
          '0%': {
            backgroundColor: 'var(--color-petrol-600)',
          },
          '15%': {
            backgroundColor: 'var(--color-petrol-500)',
          },
          '50%': {
            backgroundColor: 'var(--color-petrol-300)',
          },
          '85%': {
            backgroundColor: 'var(--color-petrol-500)',
          },
          '100%': {
            backgroundColor: 'var(--color-petrol-600)',
          },
        },
        fadeIn: {
          '0%': {
            opacity: '0',
          },
          '100%': {
            opacity: '1',
          },
        },
        'accordion-down': {
          from: {
            height: '0',
          },
          to: {
            height: 'var(--radix-accordion-content-height)',
          },
        },
        'accordion-up': {
          from: {
            height: 'var(--radix-accordion-content-height)',
          },
          to: {
            height: '0',
          },
        },
        'collapsible-down': {
          from: {
            height: '0',
          },
          to: {
            height: 'var(--radix-collapsible-content-height)',
          },
        },
        'collapsible-up': {
          from: {
            height: 'var(--radix-collapsible-content-height)',
          },
          to: {
            height: '0',
          },
        },
      },
      fontFamily: {
        serif: 'var(--font-serif)',
        sans: 'var(--font-inter)',
      },
      borderRadius: {
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 4px)',
      },
      colors: {
        background: 'hsl(var(--background))',
        card: {
          DEFAULT: 'hsl(var(--card))',
          foreground: 'hsl(var(--card-foreground))',
        },
        popover: {
          DEFAULT: 'hsl(var(--popover))',
          foreground: 'hsl(var(--popover-foreground))',
        },
        muted: {
          DEFAULT: 'hsl(var(--muted))',
          foreground: 'var(--muted-foreground)',
        },
        accent: {
          DEFAULT: 'hsl(var(--accent))',
          foreground: 'hsl(var(--accent-foreground))',
        },
        destructive: {
          DEFAULT: 'hsl(var(--destructive))',
          foreground: 'hsl(var(--destructive-foreground))',
        },
        border: 'hsl(var(--border))',
        input: 'var(--input)',
        ring: 'var(--color-primary)',
        chart: {
          '1': 'hsl(var(--chart-1))',
          '2': 'hsl(var(--chart-2))',
          '3': 'hsl(var(--chart-3))',
          '4': 'hsl(var(--chart-4))',
          '5': 'hsl(var(--chart-5))',
        },
      },
    },
  },
  // tailwindcss-aria-attributes is required until we upgrade to tailwind v4
  plugins: [
    require('tailwindcss-animation'),
    require('tailwindcss-animate'),
    require('tailwindcss-aria-attributes'),
    require('@tailwindcss/container-queries'),
  ],
  corePlugins: {
    container: false,
    preflight: false,
  },
} satisfies Config
